/** Fit a complete 4:3 drawing inside the available box, with a high-DPI backing store. */
import { DRAWING_WIDTH, DRAWING_HEIGHT } from "./coordinates.js";
import { forwardRef } from "preact/compat";
import { useEffect, useRef, useCallback } from "preact/hooks";
import type { JSX, RefObject } from "preact";

export type CanvasProps = {
  /** Called whenever the physical canvas is resized (i.e. DPR or viewport changed). */
  onResize?: (physicalWidth: number, physicalHeight: number, dpr: number) => void;
  /** Additional CSS class names. */
  class?: string;
  style?: Record<string, string | number>;
  /**
   * Pointer handlers spread onto the underlying <canvas>. Wired by
   * DrawingCanvas via usePointerHandlers. Without these, the canvas is
   * inert: this is the make-or-break wiring point for the M2 drawing
   * pipeline.
   */
  onPointerDown?: JSX.PointerEventHandler<HTMLCanvasElement>;
  onPointerMove?: JSX.PointerEventHandler<HTMLCanvasElement>;
  onPointerUp?: JSX.PointerEventHandler<HTMLCanvasElement>;
  onPointerCancel?: JSX.PointerEventHandler<HTMLCanvasElement>;
  onLostPointerCapture?: JSX.PointerEventHandler<HTMLCanvasElement>;
};

/**
 * High-DPI <canvas> that tracks its parent container's size.
 * Fits the whole 4:3 picture inside the parent.
 * Backing-store dimensions = fitted CSS size × devicePixelRatio.
 */
export const Canvas = forwardRef<HTMLCanvasElement, CanvasProps>(function Canvas(
  {
    onResize,
    class: className,
    style,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onLostPointerCapture,
  },
  ref,
) {
  const localRef = useRef<HTMLCanvasElement>(null);
  const canvasRef = (ref as RefObject<HTMLCanvasElement>) ?? localRef;
  const onResizeCb = useRef(onResize);
  onResizeCb.current = onResize;

  const applySize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    if (!parent) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = parent.getBoundingClientRect();
    const box = getComputedStyle(parent);
    const width = rect.width - (parseFloat(box.borderLeftWidth) || 0) - (parseFloat(box.borderRightWidth) || 0);
    const height = rect.height - (parseFloat(box.borderTopWidth) || 0) - (parseFloat(box.borderBottomWidth) || 0);
    const fit = Math.max(0, Math.min(width / DRAWING_WIDTH, height / DRAWING_HEIGHT));
    const cssWidth = DRAWING_WIDTH * fit;
    const cssHeight = DRAWING_HEIGHT * fit;
    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;
    canvas.style.marginLeft = `${Math.max(0, (width - cssWidth) / 2)}px`;
    canvas.style.marginTop = `${Math.max(0, (height - cssHeight) / 2)}px`;
    const physW = Math.round(cssWidth * dpr);
    const physH = Math.round(cssHeight * dpr);

    // Only re-assign if dimensions actually changed, avoid unnecessary
    // context resets which clear the canvas.
    if (canvas.width !== physW || canvas.height !== physH) {
      canvas.width = physW;
      canvas.height = physH;
      onResizeCb.current?.(physW, physH, dpr);
    }
  }, [canvasRef]);

  useEffect(() => {
    applySize();

    const ro = new ResizeObserver(applySize);
    const parent = canvasRef.current?.parentElement;
    if (parent) ro.observe(parent);

    return () => ro.disconnect();
  }, [applySize, canvasRef]);

  return (
    <canvas
      ref={canvasRef}
      class={className}
      style={{
        display: "block",
        width: "100%",
        height: "100%",
        touchAction: "none", // prevent browser pan/zoom from eating pointer events
        ...style,
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onLostPointerCapture={onLostPointerCapture}
    />
  );
});
